import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
	BottomSheet,
	BottomSheetBody,
	BottomSheetCloseButton,
	BottomSheetFooter,
	BottomSheetHeader,
} from '@anwb/poncho/components/bottom-sheet';
import { ButtonSecondary } from '@anwb/poncho/components/button';
import { useApplicationSize } from '@anwb/poncho/providers/providers-application';

import {
	setDurationPeriod,
	setIdForPricePoint,
	setKilometrageChoice,
	setPriceForPricePoint,
} from '../../../../../store/actions';
import {
	chosenDurationSelector,
	chosenKilometrageSelector,
	leaseCarGroupedPricePointsSelector,
} from '../../../../../store/selectors';
import { setCookie } from '../../../../../utils';
import { trackEvent } from '../../../../../utils/trackEvent';

import { Durations } from './Durations';
import { Kilometrage } from './Kilometrage';
import { updatePricePointForDuration } from './utils';

import { StyledModal } from './styles/specs.styled';

enum ModalType {
	duration = 'duration',
	kilometrage = 'kilometrage',
}

const modalTypeButtonTextMap: Record<ModalType, string> = {
	duration: 'looptijd',
	kilometrage: 'kilometers',
};

export const ModalOrBottomSheet = ({
	modalType,
	closeModal,
}: {
	modalType: ModalType;
	closeModal: () => void;
}) => {
	const isMobile = useApplicationSize() === 'small';
	const currentKilometrage = useSelector(chosenKilometrageSelector);
	const currentDuration = useSelector(chosenDurationSelector);
	const selectedKilometrage = useSelector(chosenKilometrageSelector);
	const [pendingValue, setPendingValue] = useState<string | undefined>(
		modalType === ModalType.kilometrage ? currentKilometrage : currentDuration
	);
	const Content = modalType === ModalType.duration ? Durations : Kilometrage;
	const dispatch = useDispatch();

	useEffect(() => {
		if (modalType === ModalType.kilometrage) {
			setPendingValue(currentKilometrage);
		}
	}, [currentKilometrage, modalType]);

	const confirmOrCancelButtonHandler = (actionValue: string, field: string) => {
		trackEvent({
			event: 'user_interaction',
			type: 'form',
			component: 'pop-up',
			field,
			action: 'click',
			actionValue: `kies-${actionValue}`,
			funnel_name: 'private-lease-nieuw',
			funnel_step: 'samenstelling-auto-en-contract',
			funnel_index: '1',
		});
	};

	const groupedPricePoints = useSelector(leaseCarGroupedPricePointsSelector);
	const selectedDuration = useSelector(chosenDurationSelector);

	const handleConfirm = () => {
		if (pendingValue !== undefined) {
			if (modalType === ModalType.duration) {
				dispatch(setDurationPeriod(pendingValue));
				setCookie('duration', pendingValue);
				updatePricePointForDuration(
					dispatch,
					pendingValue,
					groupedPricePoints,
					selectedKilometrage
				);
			} else if (modalType === ModalType.kilometrage) {
				const kilometrageObject =
					groupedPricePoints &&
					selectedDuration &&
					groupedPricePoints[Number(selectedDuration)]?.kilometrages.find(
						(km) => km.mileage.toString() === pendingValue
					);
				if (kilometrageObject) {
					dispatch(setKilometrageChoice(pendingValue));
					dispatch(setIdForPricePoint(kilometrageObject.id));
					dispatch(setPriceForPricePoint(kilometrageObject.pricePerMonth));
					setCookie('kilometrage', pendingValue);
				}
			}
		}
	};

	return isMobile ? (
		<BottomSheet
			open
			onClose={() => {
				confirmOrCancelButtonHandler('annuleren', modalType);
				closeModal();
			}}
		>
			<BottomSheetHeader>
				<div />
				<BottomSheetCloseButton />
			</BottomSheetHeader>
			<BottomSheetBody>
				<Content confirmOnChange={true} value={pendingValue} onChange={setPendingValue} />
			</BottomSheetBody>
			<BottomSheetFooter>
				<ButtonSecondary
					onClick={() => {
						confirmOrCancelButtonHandler(modalTypeButtonTextMap[modalType], modalType);
						handleConfirm();
						closeModal();
					}}
				>{`Kies ${modalTypeButtonTextMap[modalType]}`}</ButtonSecondary>
			</BottomSheetFooter>
		</BottomSheet>
	) : (
		<StyledModal
			size="l"
			open
			onOpenChange={() => {
				confirmOrCancelButtonHandler('annuleren', modalType);
				closeModal();
			}}
			variant="dialog"
			buttonVisualStyle="secondary"
			cancelButtonText="Annuleren"
			buttonText={`Kies ${modalTypeButtonTextMap[modalType]}`}
			buttonHandler={() => {
				confirmOrCancelButtonHandler(modalTypeButtonTextMap[modalType], modalType);
				handleConfirm();
				closeModal();
			}}
		>
			<Content confirmOnChange={true} value={pendingValue} onChange={setPendingValue} />
		</StyledModal>
	);
};
